import { animate, Transition } from 'motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { gameConfigStore, useGameConfigSelector } from '../store/GameConfigStore';
import { store } from '../store/Store';
import { waitForSeconds } from './Async';
import { Log } from './Logging';

export function useAnimationsDisabled(): boolean {
	return useGameConfigSelector(gameConfigStore.selectors.sensorySensitivityMode);
}

function isDisabled(): boolean {
	// Don't need a hook here, we can assume sensorySensitivityMode never changes during gameplay
	return gameConfigStore.selectors.sensorySensitivityMode(store.getState());
}

export function adaptTransition<V>(transition: Transition<V>): Transition<V> {
	return isDisabled()
		? disableAnimationTiming(transition) as Transition<V>
		: transition;

}

export function useDialogAnimation() {
	const animationsDisabled = isDisabled();

	return {
		animationsDisabled,
		dialogProps: animationsDisabled
			? { transitionDuration: 0 }
			: {},
	};
}

type WaitOptions = {
	seconds: number;
	/** If animations are disabled, wait for this long instead, if specified */
	disabledSeconds?: number;
};

export async function waitForAnimation(options: WaitOptions): Promise<void> {
	if (isDisabled()) {
		if (options?.disabledSeconds) {
			await waitForSeconds(options.disabledSeconds);
		}
	} else {
		await waitForSeconds(options.seconds);
	}
}

export function useAnimatedInteger(target: number): number {
	const [displayValue, setDisplayValue] = useState(target);
	const previous = useRef(target);
	const animationsDisabled = useAnimationsDisabled();

	useEffect(
		function animateValueChange() {
			if (animationsDisabled) {
				previous.current = target;
				setDisplayValue(target);
			} else if (previous.current !== target) {

				const steps = Math.abs(target - previous.current);
				const controls = animate(previous.current, target, {
					duration: Math.min(steps * .1, 1),
					ease: 'easeOut',
					onUpdate: (latest) => setDisplayValue(Math.round(latest)),
					onComplete: () => previous.current = target,
				});

				return () => controls.stop();
			}
		}, [animationsDisabled, target]
	);

	return displayValue;
}


export type AsyncAnimation = {
	name: string;
	play: (options?: PlayOptions) => Promise<unknown>;
	props: AsyncAnimationProps;
};

type PlayOptions = {
	direction: 'forward' | 'reverse';
};

/**
 * Props for components that can be shown/hidden with async support.
 * @see useAsyncAnimation
 */
export type AsyncAnimationProps = {
	show: boolean;
	reverse?: boolean;
	onComplete?: () => void;
};

type AsyncAnimOptions = {
	/** Sets the initial value of `show`. @default false */
	initialShow?: boolean;
	/** Sets `show` to false when the animation completes. @default true. */
	hideOnFinish?: boolean;
};

/**
 * Control React component animations from async function calls
 *
 * @example
 * const welcomeAnim = useAsyncAnimation('welcome');
 *
 * async function playAnimations() {
 *   await welcomeAnim.play();
 *   await otherAnim.play();
 * }
 * 
 * return <WelcomeComponent {...welcomeAnim} />
 */
export function useAsyncAnimation(name: string, options?: AsyncAnimOptions): AsyncAnimation {
	const [show, setShow] = useState(options?.initialShow ?? false);
	const [reverse, setReverse] = useState(false);
	const resolve = useRef<() => unknown>(undefined);
	const { hideOnFinish = true } = options ?? {};

	const play = useCallback((playOptions?: PlayOptions) => new Promise<void>((resolveArg) => {
		if (resolve.current) {
			Log.ui.warn(`Anim ${name} play() called before previous run completed`);
		}
		resolve.current = resolveArg;

		const isReversed = playOptions?.direction === 'reverse';
		setReverse(isReversed);
		setShow(true);

		Log.ui.debug(`Anim ${name} playing ${isReversed ? 'reversed' : ''}...`);
	}), []);

	return {
		name,
		play,
		props: {
			show,
			reverse,
			onComplete: () => {
				Log.ui.debug(`Anim ${name} complete`);
				setShow(s => (hideOnFinish ? false : s));
				resolve.current?.();
				resolve.current = undefined;
			},
		}
	};
}

type Directed<F, T> = { from: F, to: T; };

export function directed<F, T>(forwardValues: Directed<F, T>, props: Pick<AsyncAnimationProps, 'reverse'>): Directed<F, T> | Directed<T, F> {
	return props.reverse
		? { from: forwardValues.to, to: forwardValues.from }
		: forwardValues;
}

function disableAnimationTiming<V>(transition: Transition<V>): Transition<V> {
	if (Array.isArray(transition)) {
		return transition.map(disableAnimationTiming) as Transition<V>;
	} else if (isTransitionConfig(transition)) {
		const result: Transition<V> = {};
		for (const [key, entry] of Object.entries(transition)) {
			if (isTimingKey(key) && (typeof entry === 'number')) {
				result[key] = 0;
			} else {
				result[key as keyof Transition<V>] = disableAnimationTiming(entry);
			}
		}
		return result;
	} else {
		return transition;
	}
}

function isTransitionConfig<V>(value: unknown): value is Transition<V> {
	return (typeof value === 'object') && (value !== null) && !Array.isArray(value);
}

const TRANSITION_TIMING_KEYS = new Set(['delay', 'duration', 'repeatDelay', 'visualDuration']);

function isTimingKey(key: string): key is keyof Transition {
	return TRANSITION_TIMING_KEYS.has(key);
}